import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getLatestRelatedArticleDate } from 'src/features/singleStory/SingleStorySelector'
import Logo from 'src/components/Logo'
import Tag from 'src/components/Tag'
import FullImage from 'src/components/FullImage'
import Typography from 'src/lib/ui/Typography'
import styles from '../Article.module.sass'
import ActionButtons from 'src/features/actionButtons/ActionButtons'
import SaveButton from 'src/components/SaveButton'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import Button from 'src/components/Button'
import { FlagCircleOutlined, FlagCircleRounded } from '@mui/icons-material'
import { actions as libraryPicksActions } from 'src/features/libraryPicks/LibraryPicksSlice'
import { actions as singleStoryActions } from 'src/features/singleStory/SingleStorySlice'
import usePiwik from 'src/hooks/usePiwik'
import { siteName } from 'src/utils/oneCodeBase'
import PropTypes from 'prop-types'

dayjs.extend(utc)

const ArticleHeader = ({
  story,
  likeCount,
  liked,
  onLikeUpdate,
  title,
  configSettings,
  simplePage = false,
  userIsLibrarian = false,
  userIsLibraryAdmin = false,
  companyId = null
}) => {
  const siteId = process.env.REACT_APP_SITE_ID
  const isUpcomingReport = story?.reportSettings?.upcoming ?? false
  const dispatch = useDispatch()
  const latestArticleUpdateDate = useSelector(getLatestRelatedArticleDate())
  const wasLibraryPicked = story?.libraryPickId

  const getDisplayDate = () => {
    const publishedDate = story?.published
      ? dayjs.unix(story.published).utc().format('MMMM DD, YYYY')
      : null

    const updatedDate =
      latestArticleUpdateDate &&
      dayjs.unix(latestArticleUpdateDate).utc().format('MMMM DD, YYYY')

    return updatedDate || (latestArticleUpdateDate === null && publishedDate)
  }

  const displayDate = getDisplayDate()

  const {
    showActionBar = false,
    showUpdated = false,
    showTags = true,
    showRef = true
  } = configSettings

  const { handleTracking, handleActionTracking } = usePiwik()

  const handleLibraryPickClick = () => {
    const articleId = story?.id

    if (!articleId || !companyId || !siteId) return

    if (wasLibraryPicked) {
      dispatch(
        libraryPicksActions.removeLibraryPick({
          libraryPickId: wasLibraryPicked,
          articleId,
          OnRequestSuccess: () => {
            handleActionTracking('Site/Click/LibraryPicks/delete', {
              articleId
            })
            dispatch(
              singleStoryActions.updateStoryLibraryPickId({
                articleId,
                libraryPickId: null
              })
            )
          }
        })
      )
    } else {
      dispatch(
        libraryPicksActions.addLibraryPick({
          articleId,
          companyId,
          siteId,
          OnRequestSuccess: id => {
            handleActionTracking('Site/Click/LibraryPicks/add', { articleId })
            dispatch(
              singleStoryActions.updateStoryLibraryPickId({
                articleId,
                libraryPickId: id
              })
            )
          }
        })
      )
    }
  }

  const datePrefixLabeleHandler = () => {
    let label = ''
    if (showUpdated) {
      label = isUpcomingReport ? 'To be published' : 'Last Updated'
    } else {
      label = 'Published'
    }
    return label
  }

  return (
    <header>
      {simplePage && (
        <div className={styles.logoContainer}>
          <Logo className={styles.logo} />
        </div>
      )}
      <div>
        <div className={styles.topActions}>
          {showTags && story?.type && (
            <Tag label={story.type} to={`/river/type/${story.type}`} />
          )}
          {userIsLibrarian && wasLibraryPicked && (
            <Tag
              label={'Library Picks'}
              className={
                siteName !== 'Reports'
                  ? styles.plus
                  : styles.libraryPicksReports
              }
              to={siteName !== 'Reports' && `/river/library-picks`}
              onClick={() =>
                siteName !== 'Reports' &&
                handleTracking('article', 'type-click', 'library-picks')
              }
            />
          )}
          <SaveButton />
        </div>
        <Typography type="headline" gutterBottom className={styles.title}>
          {title}
        </Typography>
        {story?.author && <div className={styles.author}>{story.author}</div>}
        {displayDate && (
          <div className={styles.published}>
            {`${datePrefixLabeleHandler()} : ${displayDate}`}
          </div>
        )}
        {showRef && story?.source && (
          <div className={styles.source}>Ref: {story.source}</div>
        )}
        {userIsLibraryAdmin && (
          <Button
            label={`Add${wasLibraryPicked ? 'ed' : ''} to Library Picks`}
            labelColor={'#000'}
            variant="text"
            disableUppercase
            disableRipple
            disableHover
            startIcon={
              wasLibraryPicked ? (
                <FlagCircleOutlined sx={{ color: '#e27746' }} />
              ) : (
                <FlagCircleRounded sx={{ color: '#e27746' }} />
              )
            }
            onClick={handleLibraryPickClick}
          />
        )}
        {!simplePage && showActionBar && (
          <div className={styles.actionButtonsContainer}>
            <ActionButtons
              learnMoreProps={{ active: false }}
              reportSettings={story?.reportSettings}
              articleId={story?.id}
              articleBody={story?.body}
              articleTitle={title}
              singleStory={true}
              likeProps={{
                liked,
                likeCount,
                action: onLikeUpdate
              }}
            />
          </div>
        )}
      </div>
      {story?.image && (
        <div className={styles.imageContainer}>
          <FullImage
            src={story.image}
            width="100%"
            height="100%"
            objectFit="contain"
          />
        </div>
      )}
    </header>
  )
}

ArticleHeader.propTypes = {
  story: PropTypes.object,
  likeCount: PropTypes.number,
  liked: PropTypes.bool,
  onLikeUpdate: PropTypes.func,
  title: PropTypes.string.isRequired,
  configSettings: PropTypes.object,
  simplePage: PropTypes.bool,
  userIsLibrarian: PropTypes.bool,
  userIsLibraryAdmin: PropTypes.bool,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default ArticleHeader
